import { useState } from "react";
import { Mail, Send, DatabaseZap, Download } from "lucide-react";
import { toast } from "sonner";
import api, { download, errMsg } from "../lib/api";
import { useApi, Card, Loading } from "./common";
import { Button } from "./ui/button";
import { fdatetime } from "../lib/format";

function EmailCard() {
  const { data, reload } = useApi("/admin/email/status");
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/email/test", {});
      (r.data.status === "sent" ? toast.success : toast.error)(r.data.status === "sent" ? `Test email sent to ${r.data.to}` : `Not sent: ${r.data.error}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return <Loading />;
  return (
    <Card title="Email integration" testid="settings-email">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Mail className="h-5 w-5 text-primary" />
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${data.configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="email-status">
          {data.configured ? "Configured" : "Needs Gmail App Password"}</span>
        <span>Sender: <b>{data.from_name} &lt;{data.from}&gt;</b> via {data.host}:{data.port}</span>
        <Button size="sm" variant="outline" disabled={busy || !data.configured} onClick={test} data-testid="email-test-button"><Send className="h-4 w-4 mr-1" />Send test to {data.test_recipient}</Button>
      </div>
      <div className="text-xs text-muted-foreground mt-2">Last test: {data.last_test ? `${fdatetime(data.last_test.at)} — ${data.last_test.status}${data.last_test.error ? ` (${data.last_test.error})` : ""}` : "never"} · Failures (7 days): {data.failures_7d}</div>
      {data.recent.length > 0 && <ul className="mt-2 text-xs divide-y" data-testid="email-log">{data.recent.map((e) => <li key={e.id} className="py-1 flex gap-2"><span className="w-32 text-muted-foreground">{fdatetime(e.at)}</span><span className="w-36 truncate">{e.kind}</span><span className="flex-1 truncate">{e.to}</span><span className={e.status === "sent" ? "text-emerald-600" : "text-rose-600"}>{e.status}{e.error ? `: ${e.error}` : ""}</span></li>)}</ul>}
    </Card>
  );
}

function ResetCard() {
  const { data, reload } = useApi("/admin/reset/preview");
  const backups = useApi("/admin/backups");
  const [phrase, setPhrase] = useState("");
  const [dbName, setDbName] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Permanently delete ALL business records listed? A backup is created first.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/reset", { confirm: phrase, database: dbName });
      toast.success(`Reset complete. Backup saved (${r.data.backup_path.split("/").pop()})`);
      setPhrase(""); setDbName(""); reload(); backups.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!data) return <Loading />;
  return (
    <Card title="Reset business data" testid="settings-reset">
      <p className="text-sm text-muted-foreground mb-2">Deletes leads, clients, projects, tasks, invoices, payments, expenses, refunds, HR records, Lead Agent results and notifications in <b>{data.database}</b>. Keeps users, roles, settings, branding, invoice numbering and the audit log. A backup is saved first.</p>
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="reset-counts">{Object.entries(data.counts).filter(([, n]) => n).map(([k, n]) => <span key={k} className="text-xs rounded-full bg-muted px-2 py-0.5">{k}: <b>{n}</b></span>)}{data.total === 0 && <span className="text-xs text-emerald-700">All business modules are empty.</span>}</div>
      <div className="grid sm:grid-cols-3 gap-2">
        <input className="vmw-input" placeholder={`Type ${data.confirm_phrase}`} value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="reset-phrase" />
        <input className="vmw-input" placeholder={`Type database name: ${data.database}`} value={dbName} onChange={(e) => setDbName(e.target.value)} data-testid="reset-db-name" />
        <Button variant="destructive" disabled={busy || phrase !== data.confirm_phrase || dbName !== data.database || data.total === 0} onClick={run} data-testid="reset-run"><DatabaseZap className="h-4 w-4 mr-1" />{busy ? "Resetting…" : "Back up & reset"}</Button>
      </div>
      {(backups.data || []).length > 0 && <ul className="mt-3 text-xs divide-y" data-testid="backup-list">{backups.data.map((b) => (
        <li key={b.id} className="py-1.5 flex items-center gap-2"><span className="flex-1">{fdatetime(b.at)} · {b.by} · {Object.values(b.counts).reduce((a, c) => a + c, 0)} records</span>
          <button className="text-primary inline-flex items-center gap-1" onClick={() => download(`/admin/backups/${b.id}/download`, b.path.split("/").pop())} data-testid={`backup-download-${b.id}`}><Download className="h-3.5 w-3.5" />Download</button></li>))}</ul>}
    </Card>
  );
}

export const AdminTools = () => (<><EmailCard /><ResetCard /></>);
